// apps/web/src/features/crm/leads/card/ArtifactsFilesSection.tsx
//
// "Files" subsection of the "Artifacts" card (#2316, D-1). File list + row
// delete, re-housed from the retired `LeadFilesSection` (#1085). The quota
// METER header row is dropped (D-1 — the reference has no equivalent); the
// quota ERROR + cap-reached message stay. The upload TRIGGER moved to the
// card's overflow menu -> "File" item — `ArtifactsBlock` owns the hidden
// `<input type=file>` + `useLeadFileUpload`, so this subsection stays
// list + manager-only delete only.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LeadFile } from '@magimp/shared';
import { useDeleteLeadFile, useLeadFiles } from '@magimp/shared-hooks';
import { Button, ConfirmModal, ErrorState, Icon, LoadingState } from '../../../../ui/index.js';
import { formatDateTime, formatSize } from '../../../../i18n/index.js';
import { useToastStore } from '../../../../stores/toast.js';
import { errorMessage } from '../../../../api/errors.js';

export interface ArtifactsFilesSectionProps {
  leadId: string;
  canManage: boolean;
}

export function ArtifactsFilesSection({ leadId, canManage }: ArtifactsFilesSectionProps) {
  const { t } = useTranslation(['leads', 'common']);
  const filesQ = useLeadFiles(leadId);

  if (filesQ.isPending) return <LoadingState />;
  if (filesQ.error) {
    return (
      <ErrorState
        title={t('common:states.loadError')}
        text={errorMessage(filesQ.error)}
        onRetry={() => void filesQ.refetch()}
      />
    );
  }

  const { items, usage } = filesQ.data;
  // Mirrors the vendored reference's `files.length>0 && (...)` — a truly
  // empty file list contributes nothing here; `ArtifactsBlock` folds the
  // combined "nothing at all" state into the single non-proposal empty copy.
  if (items.length === 0) return null;

  const capReached = usage.count >= usage.maxPerLead;

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}
      data-testid="lead-files-section"
    >
      <div
        style={{
          fontSize: 10,
          color: 'var(--fg-4)',
          textTransform: 'uppercase',
          letterSpacing: '.08em',
        }}
      >
        {t('leads:activity.files.title')}
      </div>
      <ul
        style={{
          margin: 0,
          padding: 0,
          listStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
        data-testid="lead-files-list"
      >
        {items.map((f) => (
          <FileListRow key={f.id} leadId={leadId} file={f} canManage={canManage} />
        ))}
      </ul>
      {capReached && (
        <div style={{ fontSize: 12, color: 'var(--fg-2)' }} data-testid="lead-files-cap-reached">
          {t('leads:activity.files.capReached')}
        </div>
      )}
    </div>
  );
}

function FileListRow({
  leadId,
  file,
  canManage,
}: {
  leadId: string;
  file: LeadFile;
  canManage: boolean;
}) {
  const { t } = useTranslation(['leads', 'common']);
  const showToast = useToastStore((s) => s.show);
  const del = useDeleteLeadFile(leadId);
  const [confirming, setConfirming] = useState(false);

  const onConfirmDelete = () => {
    del.mutate(file.id, {
      onSuccess: () => {
        showToast({ tone: 'success', text: t('leads:activity.files.toasts.deleted') });
      },
      onError: (err) => {
        showToast({ tone: 'error', text: errorMessage(err) });
      },
    });
  };

  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 8px',
        borderRadius: 'var(--r-2)',
        border: '1px solid var(--border-1)',
        background: 'var(--bg-1)',
      }}
      data-testid="lead-files-row"
      data-file-id={file.id}
    >
      <Icon name="paperclip" size={14} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span
          style={{
            fontSize: 13,
            color: 'var(--fg-1)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={file.fileName}
        >
          {file.fileName}
        </span>
        <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
          {formatSize(file.contentLength)} ·{' '}
          {file.uploadedBy?.name ?? t('leads:activity.files.uploaderDeleted')} ·{' '}
          {formatDateTime(file.createdAt)}
        </span>
      </div>
      {canManage && (
        <Button
          size="sm"
          kind="ghost"
          icon="trash-2"
          aria-label={t('common:actions.delete')}
          onClick={() => setConfirming(true)}
          data-testid="lead-files-delete"
        />
      )}
      {confirming && (
        <ConfirmModal
          title={t('leads:activity.files.confirmDelete.title')}
          text={t('leads:activity.files.confirmDelete.text', { name: file.fileName })}
          danger
          confirmLabel={t('common:actions.delete')}
          onConfirm={onConfirmDelete}
          onClose={() => setConfirming(false)}
        />
      )}
    </li>
  );
}
